import type { ChartAdditionalParams } from 'shared/domain/analytics/chartModel';
import { keepIssuesOfVisibleProcesses } from '../model';
import { get as getRootCauses } from 'serviceWorker/db/fieldValues/rootCauses/rootCauses';
import { IssueEntity } from 'serviceWorker/repository/issue/entity';
import {
  ChartValuesAndLabels,
  FilterValues,
  IssueChart,
} from 'shared/types/analytics';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import {
  increaseEntitySeries,
  increaseNoneSeries,
  toSortedValuesAndLabels,
} from '../seriesCreation';
import { filterIssuesByChartFilters } from '../issueChartFilters';

export async function createSeriesAndLabelsFromIssuesWithFilters(
  issues: IssueEntity[],
  filters: FilterValues,
  timezone: string,
  additionalParams: ChartAdditionalParams | undefined
): Promise<ChartValuesAndLabels> {
  const rootCausesLabels = {};
  const rootCausesMap = (await getRootCauses()).reduce(
    (map, rootCause) => {
      map.set(rootCause._id, {
        _id: rootCause._id,
        label: rootCause.label,
      });
      return map;
    },
    new Map<string, LabelledEntity>()
  );

  const filteredIssues = keepIssuesOfVisibleProcesses(
    filterIssuesByChartFilters(
      issues,
      IssueChart.rootCause,
      filters,
      timezone
    ),
    additionalParams
  );

  const reducedToRootCauseSummary = filteredIssues.reduce<HashMap<number>>(
    (result, issue) => {
      const issueRootCauses = issue.extendedData.rootCauses || [];

      if (issueRootCauses.length === 0) {
        increaseNoneSeries(result, rootCausesLabels);
      } else {
        issueRootCauses.forEach((rootCause) => {
          increaseEntitySeries(
            result,
            rootCausesLabels,
            rootCausesMap.get(rootCause)!
          );
        });
      }

      return result;
    },
    {}
  );

  return Promise.resolve(
    toSortedValuesAndLabels(reducedToRootCauseSummary, rootCausesLabels)
  );
}
